'use client';

import { c as _c } from "react/compiler-runtime";
import { Fragment } from 'react';
import { NullState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components';
import { flexRender } from '@tanstack/react-table';
import { ExpandedRows, isRowExpanded } from './ExpandedRows';
import { LinkCell } from './LinkCell';
import PaginationFooter from './PaginationFooter';
import { SelectionActionBar } from './SelectionActionBar';
import type { NullStateProps } from '../components';
import type { DataTableActiveRow, DataTableExpansion } from './types';
import type { HeaderGroup, RowModel, RowSelectionState, SortingState } from '@tanstack/react-table';
import type { PaginationFooterProps } from './PaginationFooter';
export interface DataTableProps<TData, TChildRow = never> {
  headerGroups: HeaderGroup<TData>[];
  rowModel: RowModel<TData>;
  hideHeader?: boolean;
  inset?: boolean;
  nullStateProps?: NullStateProps;
  onRowClick?: (row: TData) => void;
  pagination?: PaginationFooterProps;
  rowClassName?: (row: TData) => string | undefined;
  rowHref?: (row: TData) => string;
  testId?: string;
  autoWidth?: boolean;
  /** Use the darkening edge shadow instead of the default fade for the horizontal-overflow cue. */
  scrollShadow?: boolean;
  /** Current sorting state; drives header accessibility and React Compiler dependencies. */
  sortingState?: SortingState;
  /**
   * Renders an always-visible selection action bar above the table (shows the
   * count, and actions that stay put and disable themselves at zero selection).
   */
  selection?: {
    selectedCount: number;
    onClear: () => void;
    /** Action controls operating on the selection (buttons, etc.). */
    actions?: React.ReactNode;
    /** Current selection state — forwarded so the body re-renders on change. */
    rowSelectionState?: RowSelectionState;
  };
  /**
   * Highlights one row as the one being viewed. A row click activates it in
   * addition to running `onRowClick`.
   */
  activeRowState?: DataTableActiveRow<TData>;
  /** Renders the expanded part of expanded rows. */
  expansion?: DataTableExpansion<TData, TChildRow>;
}
export interface DataTableBodyProps<TData, TChildRow = never> {
  headerGroups: HeaderGroup<TData>[];
  rowModel: RowModel<TData>;
  inset?: boolean;
  nullStateProps?: NullStateProps;
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string | undefined;
  rowHref?: (row: TData) => string;
  testId?: string;
  /**
   * Current row-selection state. Passed so this component re-renders (and its
   * row cells recompute) when selection changes — otherwise, under React
   * Compiler, the stable row model lets it skip re-rendering and
   * the row checkboxes go stale even though the shared state updated.
   */
  rowSelectionState?: RowSelectionState;
  /** Extra rows rendered inside the same tbody after the data rows. */
  trailingRows?: React.ReactNode;
  /**
   * Marks one row with `data-active`, and activates the clicked row. `activeRowId`
   * is read for the same reason as `rowSelectionState`: it makes this component
   * re-render when the active row changes, which the stable row model would
   * otherwise let it skip.
   */
  activeRowState?: DataTableActiveRow<TData>;
  /** Renders the expanded part of expanded rows. */
  expansion?: DataTableExpansion<TData, TChildRow>;
}
const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

/**
 * Renders only the table body (tbody) section of a DataTable.
 * Used internally by DataTable and by createTableSuspenseWrapper for progressive loading.
 */
export function DataTableBody(t0) {
  const $ = _c(23);
  const {
    headerGroups,
    rowModel,
    nullStateProps,
    onRowClick,
    rowClassName,
    rowHref,
    testId,
    inset: t1,
    rowSelectionState,
    trailingRows,
    activeRowState,
    expansion
  } = t0;
  const inset = t1 === undefined ? true : t1;
  const rows = rowModel.rows;
  let t2;
  if ($[0] !== headerGroups?.[0]?.headers || $[1] !== rows?.[0]) {
    t2 = rows?.[0]?.getVisibleCells().length || headerGroups?.[0]?.headers.length;
    $[0] = headerGroups?.[0]?.headers;
    $[1] = rows?.[0];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const columnCount = t2;
  const activeRowId = activeRowState?.activeRowId;
  const toggleOnRowClick = expansion?.toggleOnRowClick;
  let t3;
  if ($[3] !== activeRowState || $[4] !== onRowClick || $[5] !== toggleOnRowClick) {
    t3 = activeRowState || toggleOnRowClick || onRowClick ? row => {
      activeRowState?.setActiveRow(row);
      toggleOnRowClick?.(row);
      onRowClick?.(row);
    } : undefined;
    $[3] = activeRowState;
    $[4] = onRowClick;
    $[5] = toggleOnRowClick;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleRowClick = t3;
  let t4;
  if ($[7] !== activeRowId || $[8] !== activeRowState || $[9] !== columnCount || $[10] !== expansion || $[11] !== handleRowClick || $[12] !== inset || $[13] !== nullStateProps || $[14] !== rowClassName || $[15] !== rowHref || $[16] !== rowSelectionState || $[17] !== rows) {
    t4 = rows?.length ? rows.map(row_0 => {
      const href = rowHref?.(row_0.original);
      const isSelected = rowSelectionState ? Boolean(rowSelectionState[row_0.id]) : row_0.getIsSelected();
      const isActive = activeRowId != null && !!activeRowState?.isActive(row_0.original);
      const rowExpanded = expansion ? isRowExpanded(expansion.expandedState, row_0.id) : false;
      const rowElement = <TableRow className={rowClassName?.(row_0.original)} data-active={isActive || undefined} data-state={isSelected && "selected"} key={row_0.id} onClick={handleRowClick && (() => handleRowClick(row_0.original))} rowHoverable={!!handleRowClick || !!rowHref}>{row_0.getVisibleCells().map((cell, cellIndex, cellArray) => {
          const columnMeta = cell.column.columnDef.meta;
          const ownsClicks = !!columnMeta?.ownsCellClicks || !!columnMeta?.isSelectionColumn || !!columnMeta?.isExpandColumn;
          const cellContext = columnMeta?.isExpandColumn ? {
            ...cell.getContext(),
            isExpanded: rowExpanded
          } : cell.getContext();
          const content = flexRender(cell.column.columnDef.cell, cellContext);
          return <TableCell inset={inset} key={cell.id} onClick={ownsClicks ? stopPropagation : undefined} style={columnMeta?.style}>{href && !ownsClicks ? <LinkCell href={href} inset={inset} isFirst={cellIndex === 0} isLast={cellIndex === cellArray.length - 1}>{content}</LinkCell> : content}</TableCell>;
        })}</TableRow>;
      if (!expansion) {
        return rowElement;
      }
      return <Fragment key={row_0.id}>{rowElement}{row_0.getCanExpand() && rowExpanded && <ExpandedRows columnCount={columnCount} expansion={expansion} inset={inset} row={row_0} />}</Fragment>;
    }) : <TableRow><TableCell className="h-24 text-center" colSpan={columnCount}><NullState {...nullStateProps} /></TableCell></TableRow>;
    $[7] = activeRowId;
    $[8] = activeRowState;
    $[9] = columnCount;
    $[10] = expansion;
    $[11] = handleRowClick;
    $[12] = inset;
    $[13] = nullStateProps;
    $[14] = rowClassName;
    $[15] = rowHref;
    $[16] = rowSelectionState;
    $[17] = rows;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== t4 || $[20] !== testId || $[21] !== trailingRows) {
    t5 = <TableBody data-testid={testId}>{t4}{trailingRows}</TableBody>;
    $[19] = t4;
    $[20] = testId;
    $[21] = trailingRows;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
}
export function DataTable(t0) {
  const $ = _c(34);
  const {
    headerGroups,
    rowModel,
    hideHeader,
    nullStateProps,
    onRowClick,
    pagination,
    rowClassName,
    rowHref,
    testId,
    inset: t1,
    autoWidth: t2,
    scrollShadow,
    selection,
    sortingState,
    activeRowState,
    expansion
  } = t0;
  const inset = t1 === undefined ? true : t1;
  const autoWidth = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== selection) {
    t3 = selection && <SelectionActionBar className="mb-3" onClear={selection.onClear} selectedCount={selection.selectedCount}>{selection.actions}</SelectionActionBar>;
    $[0] = selection;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = hideHeader ? "invisible h-0! leading-0 [&_th]:h-0! [&_th]:p-0!" : "";
  let t5;
  if ($[2] !== autoWidth || $[3] !== headerGroups || $[4] !== sortingState) {
    let t6;
    if ($[6] !== autoWidth || $[7] !== sortingState) {
      t6 = headerGroup => <TableRow key={headerGroup.id}>{headerGroup.headers.map(header => {
          const headerWidth = header.getSize();
          const hasExplicitWidth = !autoWidth && headerWidth !== 150;
          const sortEntry = sortingState?.find(t7 => {
            const {
              id
            } = t7;
            return id === header.column.id;
          });
          const ariaSort = sortEntry && (sortEntry.desc ? "descending" : "ascending");
          return <TableHead className="group" aria-sort={ariaSort} key={header.id} style={{
            ...(hasExplicitWidth && {
              width: headerWidth,
              minWidth: headerWidth
            }),
            ...header.column.columnDef.meta?.style
          }}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</TableHead>;
        })}</TableRow>;
      $[6] = autoWidth;
      $[7] = sortingState;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    t5 = headerGroups.map(t6);
    $[2] = autoWidth;
    $[3] = headerGroups;
    $[4] = sortingState;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <TableHeader className={t4}>{t5}</TableHeader>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = selection?.rowSelectionState;
  let t8;
  if ($[12] !== activeRowState || $[13] !== expansion || $[14] !== headerGroups || $[15] !== inset || $[16] !== nullStateProps || $[17] !== onRowClick || $[18] !== rowClassName || $[19] !== rowHref || $[20] !== rowModel || $[21] !== t7 || $[22] !== testId) {
    t8 = <DataTableBody activeRowState={activeRowState} expansion={expansion} headerGroups={headerGroups} inset={inset} nullStateProps={nullStateProps} onRowClick={onRowClick} rowClassName={rowClassName} rowHref={rowHref} rowModel={rowModel} rowSelectionState={t7} testId={testId} />;
    $[12] = activeRowState;
    $[13] = expansion;
    $[14] = headerGroups;
    $[15] = inset;
    $[16] = nullStateProps;
    $[17] = onRowClick;
    $[18] = rowClassName;
    $[19] = rowHref;
    $[20] = rowModel;
    $[21] = t7;
    $[22] = testId;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== scrollShadow || $[25] !== t6 || $[26] !== t8) {
    t9 = <Table scrollShadow={scrollShadow}>{t6}{t8}</Table>;
    $[24] = scrollShadow;
    $[25] = t6;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== pagination) {
    t10 = pagination && <PaginationFooter {...pagination} />;
    $[28] = pagination;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t3 || $[32] !== t9) {
    t11 = <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">{t3}{t9}{t10}</div>;
    $[30] = t10;
    $[31] = t3;
    $[32] = t9;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
}